import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  forwardRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import plotSchema from '@galo-monitor/forms/schemas/farm/plot.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PropertyActions } from '@galo-monitor/stores/property'
import { selectCurrentProperty, selectUnitOfMeasurement } from '@galo-monitor/stores/property/selectorProperty'
import colors from '@galo-monitor/styles/colors'
import { PLOT_CLASSIFICATION_TYPES } from '@galo-monitor/utils/constants'
import { getPolygonCenter } from '@galo-monitor/utils/maps'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'
import GoogleMaps from '@galo-monitor/web-components/Maps/Google'
import DrawingManager from '@galo-monitor/web-components/Maps/Google/components/DrawingManager'
import PopupPolygon from '@galo-monitor/web-components/Maps/Google/components/PopupPolygon'
import withGoogleMaps from '@galo-monitor/web-components/Maps/Google/hoc/withGoogleMaps'

const PlotForm = withGoogleMaps(props => {
  const {
    plot,
    loading,
    setLoading,
    setBlockForm,
    onSuccess,
    onError,
    formRef,
    isGoogleMapsLoaded
  } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()
  const currentProperty = useSelector(selectCurrentProperty)
  const { initials: unitInitials } = useSelector(selectUnitOfMeasurement)

  const { data: plotData, error: plotError, isValidating } = useGaloMonitorApi(plot.id ? `/farms/plots/properties/${ plot.id }` : null)
  const { data: plotTypes } = useGaloMonitorApi('/farms/plots/types')
  const { data: existingPlots } = useGaloMonitorApi(plot.id ? null : '/farms/plots/properties')

  const mounted = useRef(true)
  const polygonRef = useRef(null)

  const plotTypeOptions = useMemo(
    () => map(plotTypes, plotType => ({
      label: t('plot classification types', { type: PLOT_CLASSIFICATION_TYPES[plotType.name] }),
      value: plotType.id.toString()
    })),
    [plotTypes, t]
  )

  const polygonData = useMemo(() => {
    if (!isEmpty(plotData?.coordinates)) {
      return map(
        plotData.coordinates,
        point => ({ lat: +point.lat, lng: +point.long })
      )
    }

    if (!isEmpty(plot?.coordinates)) {
      return map(
        plot.coordinates,
        point => ({ lat: +point.lat, lng: +point.long })
      )
    }

    return []
  }, [plot, plotData])

  const existingPlotsData = useMemo(() => {
    if (!isEmpty(existingPlots)) {
      return map(
        existingPlots,
        ({ plot: existingPlot }) => {
          if (!isEmpty(existingPlot.coordinates))
            return {
              id: existingPlot.id,
              name: existingPlot.name,
              paths: map(existingPlot.coordinates, point => ({
                lat: +point.lat,
                lng: +point.long
              }))
            }
          return []
        }
      )
    }

    return []
  }, [existingPlots])

  const mapCenter = React.useMemo(
    () => {
      if (!isEmpty(plotData?.coordinates)) {
        return getPolygonCenter(
          map(plotData.coordinates, path => ({
            latitude: +path.lat,
            longitude: +path.long
          }))
        )
      }

      if (!isEmpty(plot?.coordinates)) {
        return getPolygonCenter(
          map(plot.coordinates, path => ({
            latitude: +path.lat,
            longitude: +path.long
          }))
        )
      }

      return {
        lat: +currentProperty.lat,
        lng: +currentProperty.long
      }
    },
    [currentProperty.lat, currentProperty.long, plot, plotData]
  )

  const onToggleLockMap = useCallback(() => setBlockForm(prevState => !prevState), [setBlockForm])

  const handleSubmit = useCallback(
    data => {
      setLoading(true)
      const coordinates = map(
        polygonRef.current.props.paths,
        polygon => ({
          lat: polygon.lat,
          long: polygon.lng
        })
      )

      const { area } = polygonRef.current.props

      dispatch(PropertyActions.savePropertyPlot(
        {
          ...plot,
          ...data,
          area: area[unitInitials],
          area_m: area.m2,
          coordinates
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('plot', { howMany: 1 }),
            action: toLower(t(isEmpty(plot) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [dispatch, onError, onSuccess, plot, setLoading, snackbar, t, unitInitials]
  )

  useEffect(() => () => {
    mounted.current = false
  }, [])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ plotSchema }
      onSubmit={ handleSubmit }
    >
      <Grid container spacing={ 2 }>
        <Grid item xs={ 3 }>
          <InputText
            name="name"
            label={ t('name of {this}', { this: t('plot', { howMany: 1 }), gender: 'male', howMany: 1 }) }
            defaultValue={ plot.name }
            disabled={ loading }
            fullWidth
          />

          <InputSelect
            name="plot_type_id"
            label={ t('plot classification') }
            options={ plotTypeOptions }
            defaultValue={ plot.plotTypeId?.toString() ?? '' }
            disabled={ loading }
            loading={ isValidating }
            fullWidth
          />

          <InputText
            name="observation"
            label={ t('observation', { howMany: 2 }) }
            disabled={ loading }
            defaultValue={ plot.observation ?? '' }
            fullWidth
          />
        </Grid>

        <Grid item xs={ 9 }>
          <div style={ { flex: 1, display: 'flex', flexDirection: 'column' } }>
            <div style={ { height: '60vh' } }>
              <GoogleMaps
                isLoaded={ isGoogleMapsLoaded }
                mapContainerStyle={ {
                  width: '100%',
                  height: '60vh'
                } }
                center={ mapCenter }
              >
                <DrawingManager
                  ref={ polygonRef }
                  isLoadingOverlayData={ (!plotData && !plotError) || isValidating }
                  onToggleLockMap={ onToggleLockMap }
                  overlayData={ polygonData }
                  overlayArea={ plotData?.areaM }
                  convertUnit={ unitInitials }
                />
                <>
                  {
                    map(existingPlotsData, existingPlot => (
                      <PopupPolygon
                        key={ existingPlot.id }
                        paths={ existingPlot.paths }
                        options={ {
                          fillOpacity: 0.2,
                          strokeOpacity: 0.8,
                          strokeWeight: 2,
                          fillColor: colors.redDark,
                          strokeColor: colors.redDark
                        } }
                      >
                        <div>{existingPlot.name}</div>
                      </PopupPolygon>
                    ))
                  }
                </>
              </GoogleMaps>
            </div>
          </div>
        </Grid>
      </Grid>
    </Form>
  )
})

PlotForm.propTypes = {
  plot: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  setBlockForm: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

PlotForm.defaultProps = {
  plot: {},
  loading: false,
  setLoading: () => {},
  setBlockForm: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default forwardRef((props, formRef) => <PlotForm formRef={ formRef } { ...props } />)
